/**
 * Общие части HTTP-слоя: ошибки API (ApiError из @dodoc/api-client), Retry-After для 429/503,
 * сетевые ошибки и запрос к эндпоинтам, которых ещё нет в OpenAPI (docs/contracts/backend-conventions.md, раздел 3).
 */
import { ApiError, isApiErrorBody } from '@dodoc/api-client';

export type HttpFetch = (request: Request) => Promise<Response>;

const retryAfterByError = new WeakMap<ApiError, number>();

/** Секунды из заголовка Retry-After (число или HTTP-дата). */
export function parseRetryAfter(header: string | null, now: number = Date.now()): number | null {
  if (!header) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
  const date = Date.parse(header);
  if (Number.isNaN(date)) return null;
  return Math.max(0, Math.ceil((date - now) / 1000));
}

export function attachRetryAfter(error: ApiError, response: Response): ApiError {
  const seconds = parseRetryAfter(response.headers.get('Retry-After'));
  if (seconds !== null) retryAfterByError.set(error, seconds);
  return error;
}

/** Через сколько секунд можно повторить (для 429 RATE_LIMITED и 503). */
export function retryAfterSeconds(error: unknown): number | null {
  return error instanceof ApiError ? (retryAfterByError.get(error) ?? null) : null;
}

export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  return attachRetryAfter(new ApiError(response.status, isApiErrorBody(body) ? body : null), response);
}

export function networkError(): ApiError {
  return new ApiError(0, { error: { code: 'NETWORK_ERROR', message: 'Network error', params: {}, requestId: null } });
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Результат openapi-fetch → данные или ApiError (с Retry-After). */
export async function call<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const result = await promise;
  if (result.error !== undefined || !result.response.ok) {
    throw attachRetryAfter(new ApiError(result.response.status, isApiErrorBody(result.error) ? result.error : null), result.response);
  }
  return result.data as T;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface HttpRequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export type HttpRequest = <T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, options?: HttpRequestOptions) => Promise<T>;

/**
 * Запрос к эндпоинту, которого ещё нет в сгенерированном клиенте (например, identity пишется параллельно).
 * Проходит через тот же authFetch: токен, X-Clinic-Id, X-Locale, обновление сессии, MFA_REQUIRED.
 */
export function createHttpRequest(deps: { baseUrl: string; fetch: HttpFetch; getHeaders: () => Record<string, string> }): HttpRequest {
  return async function request<T>(method: string, path: string, options: HttpRequestOptions = {}): Promise<T> {
    const url = new URL(path, deps.baseUrl.endsWith('/') ? deps.baseUrl : `${deps.baseUrl}/`);
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== null && value !== undefined) url.searchParams.set(key, String(value));
    }
    const headers = new Headers({ Accept: 'application/json', ...deps.getHeaders(), ...(options.headers ?? {}) });
    let body: string | undefined;
    if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(options.body);
    }
    const response = await deps.fetch(new Request(url, { method, headers, body, signal: options.signal, credentials: 'include' }));
    if (!response.ok) throw await apiErrorFromResponse(response);
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  } as HttpRequest;
}
